/**
 * API Client
 * Base Axios instance with auth and error handling
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { supabase } from '@/lib/supabase';
import { extractHttpErrorMessage } from './errorMessage';

// API Base URL - for production deployment on separate domain (e.g., MinIO static hosting)
// In development, this is empty (uses Vite proxy)
// In production, this should be set to the API server URL (e.g., https://agent-api.doratech.cn)
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Error type produced by the Axios interceptor.
 * Plain Error objects can't carry HTTP metadata, so we attach it here.
 * Use (err as HttpError).statusCode to detect specific status codes.
 */
export interface HttpError extends Error {
  statusCode?: number;
  responseData?: unknown;
}

// Keep admin_token in sync whenever Supabase auto-refreshes (TOKEN_REFRESHED fires ~30s before expiry).
// Without this listener, admin_token stays stale after auto-refresh rotates the refresh token,
// causing the reactive 401→refreshSession() path to fail because the old refresh token is revoked.
//
// SIGNED_OUT is intentionally NOT handled here — see LoginPage.tsx:51-54. Handling it here would
// set admin_redirect_after_login = '/login' during logout, causing a redirect loop on next login.
// Logout redirects are managed by: Sidebar/Header handleLogout, and the 401 interceptor below.
supabase.auth.onAuthStateChange((event, session) => {
  if ((event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') && session?.access_token) {
    // Note: on explicit login, LoginPage.verifyAndStoreSession() also writes admin_token.
    // This listener fires first (before /v1/admin/verify completes), so the token is set
    // regardless of admin check outcome. That is intentional: the 401 interceptor below
    // is the backstop if the user is not actually an admin.
    localStorage.setItem('admin_token', session.access_token);
  }
});

// Token refresh state (prevent concurrent refresh)
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Helper to get auth headers
const getAuthHeaders = () => {
  // Get user ID and token from localStorage (matching old admin key names)
  const userId = localStorage.getItem('admin_user_id') ||
                 sessionStorage.getItem('admin_user_id') ||
                 localStorage.getItem('user_id');
  const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');

  return { userId, token };
};

// Refresh token function
const refreshToken = async (): Promise<string | null> => {
  // Prevent concurrent refresh attempts
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // Get current session and refresh it
      const { data, error } = await supabase.auth.refreshSession();

      if (error || !data.session) {
        console.error('Token refresh failed:', error);
        return null;
      }

      // Update stored token
      const newToken = data.session.access_token;
      localStorage.setItem('admin_token', newToken);

      console.log('Token refreshed successfully');
      return newToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Create request interceptor
const createRequestInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use(
    (config) => {
      const { userId, token } = getAuthHeaders();

      if (userId) {
        config.headers['X-User-ID'] = userId;
      }

      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

// Create response interceptor with auto token refresh
const createResponseInterceptor = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        console.log('Received 401, attempting to refresh token...');

        // Try to refresh the token
        const newToken = await refreshToken();

        if (newToken) {
          // Update the authorization header with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Retry the original request
          console.log('Retrying request with new token...');
          return client(originalRequest);
        } else {
          // Refresh failed - redirect to login
          console.log('Token refresh failed, redirecting to login...');
          localStorage.removeItem('admin_token');
          sessionStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user_id');
          localStorage.removeItem('admin_email');
          localStorage.removeItem('admin_username');
          // Save current hash path so login page redirects back after login
          sessionStorage.setItem('admin_redirect_after_login', window.location.hash.slice(1) || '/');
          window.location.hash = '#/login';
          return Promise.reject(error);
        }
      }

      const responseData = error.response?.data;
      const message = extractHttpErrorMessage(responseData, error.message);
      const httpErr = new Error(message) as HttpError;
      if (error.response) {
        httpErr.statusCode = error.response.status;
        httpErr.responseData = responseData;
      }
      return Promise.reject(httpErr);
    }
  );
};

// Admin API client (for /v1/admin/* endpoints)
export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/v1/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});
createRequestInterceptor(apiClient);
createResponseInterceptor(apiClient);

// Base API client (for /v1/* endpoints like /v1/images/*)
// Longer timeout for AI operations (image generation, chat completions, etc.)
export const baseApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes for AI operations
});
createRequestInterceptor(baseApiClient);
createResponseInterceptor(baseApiClient);

export default apiClient;
